import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { AppInsightsService } from './service/app-insights.service';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Global error handler that catches all uncaught errors in Angular components
 * and logs them to Application Insights
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  
  constructor(private injector: Injector) {}

  handleError(error: Error | HttpErrorResponse): void {
    // Get AppInsightsService using injector to avoid circular dependency
    const appInsights = this.injector.get(AppInsightsService);
    
    // Log to console for development
    console.error('🔴 Global Error Caught:', error);

    // Determine error type and log appropriately
    if (error instanceof HttpErrorResponse) {
      // HTTP errors are already handled by the interceptor
      // but we log them here as well for redundancy
      appInsights.logHttpError(
        'UNKNOWN',
        error.url || 'unknown',
        error.status,
        error.statusText,
        error.message,
        error.error,
        {
          errorType: 'HttpErrorResponse',
          isGlobalHandler: 'true'
        }
      );
    } else {
      // Client-side or application errors
      const customProperties = {
        errorType: error.name || 'Error',
        componentStack: this.getComponentStack(error),
        isGlobalHandler: 'true'
      };

      appInsights.logError(error, customProperties);
    }

    // Re-throw the error in development to maintain normal error flow
    if (!this.isProduction()) {
      throw error;
    }
  }

  /**
   * Extract component stack information if available (Angular-specific)
   */
  private getComponentStack(error: any): string {
    if (error && error.ngDebugContext && error.ngDebugContext.component) {
      return error.ngDebugContext.component.constructor.name;
    }
    return 'N/A';
  }

  /**
   * Check if running in production
   */
  private isProduction(): boolean {
    // Try to check environment, but use try-catch to avoid errors during initialization
    try {
      const env = this.injector.get('environment' as any) as any;
      return env && typeof env.production === 'boolean' ? env.production : false;
    } catch {
      return false;
    }
  }
}
